import { Component, OnInit } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AuthService } from '@features/auth/services/auth.service';
import { Permission } from '@features/auth/models/auth.model';

// Interfaz para items del menú
interface MenuItem {
  label: string;
  route: string;
  icon: string;
  permissionCode: string;
}

@Component({
  selector: 'app-main-layout',
  templateUrl: './main-layout.component.html',
  styleUrls: ['./main-layout.component.scss']
})
export class MainLayoutComponent implements OnInit {
  menuCollapsed = false;
  userName: string = '';
  userLastName: string = '';
  userInitials: string = '';
  pageTitle: string = 'Inicio';
  
  // Items del menú con sus permisos requeridos
  allMenuItems: MenuItem[] = [
    { label: 'Inicio', route: '/dashboard', icon: 'fa-home', permissionCode: 'dashboard' },
    { label: 'Usuarios', route: '/usuarios', icon: 'fa-users', permissionCode: 'users' },
    { label: 'Movimientos financieros', route: '/finanzas', icon: 'fa-chart-line', permissionCode: 'finances' },
    { label: 'Gestión de financiamientos', route: '/financiamientos', icon: 'fa-credit-card', permissionCode: 'financing' },
    { label: 'Gestión de servicios', route: '/servicios', icon: 'fa-concierge-bell', permissionCode: 'services' },
    { label: 'Gestión de cursos', route: '/cursos', icon: 'fa-graduation-cap', permissionCode: 'courses' },
    { label: 'Gestión de módulos', route: '/modulos', icon: 'fa-cubes', permissionCode: 'modules' },
    { label: 'Gestión de matrículas', route: '/matriculas', icon: 'fa-user-graduate', permissionCode: 'enrollments' },
    { label: 'Gestión documental', route: '/documentos', icon: 'fa-file-alt', permissionCode: 'documents' },
    { label: 'Notificaciones', route: '/notificaciones', icon: 'fa-bell', permissionCode: 'notifications' }
  ];
  
  // Items del menú filtrados según permisos del usuario
  visibleMenuItems: MenuItem[] = [];

  constructor(
    private router: Router,
    private authService: AuthService
  ) {}
  
  ngOnInit(): void {
    // Obtener datos del usuario logueado
    this.loadUserData();
    
    // Filtrar menú según permisos del usuario
    this.filterMenuByPermissions();
    
    // Detectar cambios en la ruta para actualizar el título
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe((event) => {
      this.updatePageTitle(event.url);
    });
  }

  /**
   * Filtra los items del menú según los permisos del usuario
   */
  private filterMenuByPermissions(): void {
    const userPermissions = this.authService.getUserPermissions();
    
    // Si el usuario es admin, mostrar todo el menú
    const user = this.authService.getCurrentUser();
    if (user && user.role && user.role.toLowerCase() === 'admin') {
      this.visibleMenuItems = this.allMenuItems;
      return;
    }
    
    // Filtrar items del menú según permisos del usuario
    this.visibleMenuItems = this.allMenuItems.filter(item => {
      return userPermissions.some(p => p.code === item.permissionCode && p.is_active);
    });
    
    }

  /**
   * Actualiza el título de la página según la ruta
   */
  private updatePageTitle(url: string): void {
    if (url.includes('/notificaciones')) {
      this.pageTitle = 'Notificaciones';
    } else if (url.includes('/usuarios')) {
      this.pageTitle = 'Gestión de Usuarios';
    } else if (url.includes('/dashboard')) {
      this.pageTitle = 'Inicio';
    } else if (url.includes('/finanzas')) {
      this.pageTitle = 'Movimientos financieros';
    } else if (url.includes('/financiamientos')) {
      this.pageTitle = 'Gestión de financiamientos';
    } else if (url.includes('/servicios')) {
      this.pageTitle = 'Gestión de servicios';
    } else if (url.includes('/matriculas')) {
      this.pageTitle = 'Gestión de matrículas';
    } else if (url.includes('/cursos')) {
      this.pageTitle = 'Gestión de cursos';
    } else if (url.includes('/modulos')) {
      this.pageTitle = 'Gestión de módulos';
    } else if (url.includes('/documentos')) {
      this.pageTitle = 'Gestión documental';
    }
  }

  toggleMenu(): void {
    this.menuCollapsed = !this.menuCollapsed;
  }

  /**
   * Carga los datos del usuario logueado
   */
  private loadUserData(): void {
    const currentUser = this.authService.getCurrentUser();
    if (currentUser) {
      this.userName = currentUser.name || '';
      this.userLastName = currentUser.last_name || '';
      this.calculateInitials();
    } else {
      // Valores por defecto si no hay usuario
      this.userName = 'Usuario';
      this.userLastName = '';
      this.userInitials = 'U';
    }
  }

  /**
   * Calcula las iniciales del usuario basado en nombre y apellido
   */
  private calculateInitials(): void {
    let initials = '';
    
    if (this.userName) {
      initials += this.userName.charAt(0).toUpperCase();
    }
    
    if (this.userLastName) {
      initials += this.userLastName.charAt(0).toUpperCase();
    }
    
    // Si no hay iniciales, usar 'U' por defecto
    this.userInitials = initials || 'U';
  }

  /**
   * Obtiene el nombre completo del usuario
   */
  get fullUserName(): string {
    const name = this.userName || '';
    const lastName = this.userLastName || '';
    return `${name} ${lastName}`.trim() || 'Usuario';
  }

  logout(): void {
    this.authService.logout().subscribe(() => {
      // El AuthService ya maneja la redirección
    });
  }
}
